import { iconCloseSmall, iconSearch } from '@tesla/design-system-icons';
import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Icon, IconButton, ListBox, Tooltip, useId, useListBoxState, useTooltipState, } from '../index.js';
import { useFilterState } from './use-filter-state.js';
export const FormInputSearch = ({ className, collapsible, defaultValue = '', externallyControlled, externallyFiltered, focusOnHotkey, highlighted, id, inputRef, keysToFilterBy, listboxProps, name, onChange, onClear, onClick, onKeyDown, onOptionSelect, open, options, readOnly, selected: externalSelected, tooltipProps, value: externalValue, variant = 'default', ...rest }) => {
    const dropdownState = useTooltipState();
    const { filterValue, filteredOptions, setFilterValue } = useFilterState({
        defaultValue,
        externallyFiltered,
        keysToFilterBy,
        options,
    });
    const listBoxState = useListBoxState();
    const uid = useId();
    const htmlId = id || uid;
    // When a search has an array passed in, the tooltip wrapper should be applied, but hidden to preserve html / focus behavior.
    // If no options at all, never apply the tooltip wrapper to reduce markup.
    const enableDropdown = Boolean(options);
    const hideDropdown = Boolean(filteredOptions && !filteredOptions.length);
    const listboxRef = useRef(null);
    const containerRef = useRef(null);
    const fallbackRef = useRef(null);
    const ref = inputRef || fallbackRef;
    const [expanded, setExpanded] = useState(false);
    // Save a ref to the current state values, necessary to ensure we get up to date
    // values for each piece of state inside the event listeners
    // Without a ref, these get stuck using old values from when the listener
    // was initialized
    const stateRef = useRef({ dropdownState, enableDropdown, expanded });
    // Update the values on each render
    stateRef.current = { dropdownState, enableDropdown, expanded };
    // When selected, update the search value with that option's label
    const handleOptionSelect = (rawOption) => {
        const option = filteredOptions === null || filteredOptions === void 0 ? void 0 : filteredOptions.find((opt) => opt === rawOption);
        if (option === null || option === void 0 ? void 0 : option.label)
            setFilterValue(String(option.label));
        if (onOptionSelect)
            onOptionSelect({ value: String(option === null || option === void 0 ? void 0 : option.value) });
        dropdownState.setExpanded(false);
    };
    // Needs to run before the keystroke adds/removes any characters
    const handleKeyDown = (e) => {
        var _a;
        if (enableDropdown) {
            switch (e.key) {
                case ' ':
                case 'ArrowDown':
                case 'ArrowUp':
                case 'Enter':
                case 'Return':
                    // In some cases the input can be collapsed but still focused
                    // If it's collapsed, just open it and wait for another keypress
                    if (collapsible && !stateRef.current.expanded) {
                        setExpanded(true);
                    }
                    // Otherwise, open the dropdown
                    else {
                        dropdownState.setExpanded(true);
                    }
                    break;
                case 'Escape':
                    // First press of escape should only clear the value
                    // second should close tooltip
                    if (!((_a = ref.current) === null || _a === void 0 ? void 0 : _a.value)) {
                        dropdownState.setExpanded(false);
                        if (collapsible && stateRef.current.expanded) {
                            setTimeout(() => {
                                setExpanded(false);
                            }, 333);
                        }
                    }
                    break;
                case 'Tab':
                    dropdownState.setExpanded(false);
                    break;
                default:
                    // Make sure the options are visible if the user is typing
                    dropdownState.setExpanded(true);
                    break;
            }
        }
        if (onKeyDown) {
            onKeyDown(e);
        }
    };
    // Window-specific events
    useEffect(() => {
        const handleHotkeys = (e) => {
            var _a;
            if (focusOnHotkey && (e.key === 'k' || e.key === '/') && e.metaKey) {
                (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
                setExpanded(true);
            }
        };
        window.addEventListener('keydown', handleHotkeys);
        // Collapse input when user moves away
        const handleWindowClick = (e) => {
            var _a, _b;
            // @ts-expect-error - React's event types are not self-compatible in this case
            if (!((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
                stateRef.current.dropdownState.setExpanded(false);
                // If the dropdown is open, let it mostly close before starting to
                // collapse the input
                if (collapsible && !((_b = ref.current) === null || _b === void 0 ? void 0 : _b.value)) {
                    if (stateRef.current.dropdownState.expanded) {
                        setTimeout(() => {
                            setExpanded(false);
                        }, 333);
                    }
                    else {
                        setExpanded(false);
                    }
                }
            }
        };
        if (stateRef.current.dropdownState.expanded) {
            window.addEventListener('click', handleWindowClick);
        }
        else {
            window.removeEventListener('click', handleWindowClick);
        }
        return () => {
            window.removeEventListener('keydown', handleHotkeys);
            window.removeEventListener('click', handleWindowClick);
        };
    }, [stateRef.current.dropdownState.expanded, focusOnHotkey, collapsible, ref]);
    // // Handle clicks within the component
    const handleContainerClick = (e) => {
        var _a;
        // Focus input if shell clicked
        if (!e.target.closest('button, .tds-tooltip')) {
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
            // Dont toggle, because users may click in and out of input to edit
            // In which case we don't want to toggle the input open and closed
            // while they're just trying to do their thing
            if (!collapsible || stateRef.current.expanded) {
                dropdownState.setExpanded(true);
            }
        }
        if (collapsible)
            setExpanded(true);
        if (onClick) {
            // @ts-expect-error - React's event types are not self-compatible in this case
            onClick(e);
        }
    };
    // Update states w/ change
    const handleChange = (e) => {
        // @ts-expect-error - React's event types are not self-compatible in this case
        setFilterValue(e.target.value);
        if (onChange) {
            // @ts-expect-error - React's event types are not self-compatible in this case
            onChange(e);
        }
    };
    // Clear input state
    const handleClear = (e) => {
        var _a;
        setFilterValue('');
        listBoxState.onOptionSelect({ value: '' });
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
        dropdownState.setExpanded(true);
        if (onClear) {
            // @ts-expect-error - React's event types are not self-compatible in this case
            onClear(e);
        }
    };
    // Group props that only apply when dropdown is enabled
    const dropdownInputProps = enableDropdown
        ? {
            'aria-activedescendant': listBoxState.activeDescendant,
            'aria-expanded': open !== null && open !== void 0 ? open : dropdownState.expanded,
            autoComplete: 'off',
            role: 'combobox',
        }
        : {};
    const coreInput = (React.createElement(React.Fragment, null,
        React.createElement("div", { className: cx('tds-form-input', {
                [`tds-form-input--${variant}`]: variant,
                'tds-form-input--collapsed': collapsible && !expanded,
                'tds-form-input--highlighted': highlighted,
            }, className), "data-tds-value": (externalValue !== null && externalValue !== void 0 ? externalValue : filterValue) || undefined, 
            // @ts-expect-error - React's event types are not self-compatible in this case
            onClick: handleContainerClick, ref: containerRef },
            React.createElement("div", { className: "tds-form-input-leading" }, React.createElement(Icon, { data: iconSearch })),
            React.createElement("input", { ...dropdownInputProps, "aria-activedescendant": listBoxState.activeDescendant, className: "tds-form-input-search", id: htmlId, name: name, onChange: handleChange, onFocus: () => dropdownState.setExpanded(true), onKeyDown: handleKeyDown, readOnly: externallyControlled || readOnly, ref: ref, type: "search", value: externalValue !== null && externalValue !== void 0 ? externalValue : filterValue, ...rest }),
            React.createElement("div", { className: "tds-form-input-trailing tds-form-input-search-clear" },
                React.createElement(IconButton
                // Per ARIA 1.2, elements who's function is redundant with the keyboard
                // operation should be removed from the tab sequence
                // @ts-expect-error - React's event types are not self-compatible in this case
                , { 
                    // Per ARIA 1.2, elements who's function is redundant with the keyboard
                    // operation should be removed from the tab sequence
                    // @ts-expect-error - React's event types are not self-compatible in this case
                    onClick: handleClear, size: "medium", tabIndex: -1 },
                    React.createElement(Icon, { data: iconCloseSmall })))),
        enableDropdown && (React.createElement(Tooltip, { open: open !== null && open !== void 0 ? open : (!hideDropdown && dropdownState.expanded), orientation: "down", width: "full", ...tooltipProps, className: cx('tds-dropdown', tooltipProps === null || tooltipProps === void 0 ? void 0 : tooltipProps.className) },
            React.createElement(ListBox, { ...listBoxState, inputRef: ref, listboxRef: listboxRef, onOptionSelect: handleOptionSelect, options: filteredOptions, selected: externalSelected !== null && externalSelected !== void 0 ? externalSelected : listBoxState.selected, ...listboxProps })))));
    // Only wrap search in a tooltip wrapper if it has options.
    // - Search can itself be present within a tooltip, which creates layout bugs in Dropdowns - AWSM-7248
    // - If we change the structure with every change to `enableDropdown` however, we trigger a full re-render
    // and lose keyboard focus, so this wrapper should simply be rendered whenever there _might_ be a tooltip.
    return enableDropdown ? React.createElement("div", { className: "tds-tooltip-wrapper" }, coreInput) : coreInput;
};
